/**
 * Purchase Order API Service - RTK Query
 * BuilderBel Construction Project Management System
 */

import { baseApi } from './api';
import { ApiResponse } from '../types';
import type {
  ApprovalRequest,
  BulkStatusUpdateRequest,
  CreateLineItemRequest,
  CreatePurchaseOrderRequest,
  PurchaseOrder,
  PurchaseOrderListParams,
  PurchaseOrderListResponse,
  PurchaseOrderSummaryStats,
  SendPurchaseOrderRequest,
  UpdateLineItemRequest,
  UpdatePurchaseOrderRequest,
  UpdateStatusRequest,
  VendorPurchaseOrderSummary,
  // Receipt types
  Receipt,
  ReceiptListParams,
  ReceiptListResponse,
  ReceiptSummary,
  CreateReceiptRequest,
  UpdateReceiptRequest,
  ConfirmReceiptRequest,
  // Change Order types
  ChangeOrder,
  ChangeOrderListParams,
  ChangeOrderListResponse,
  ChangeOrderSummary,
  CreateChangeOrderRequest,
  UpdateChangeOrderRequest,
  ApproveChangeOrderRequest,
  RejectChangeOrderRequest,
} from '../types/purchaseOrder';

/**
 * Purchase Order API Endpoints
 */
export const purchaseOrderApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // ============================================================================
    // PURCHASE ORDER CRUD
    // ============================================================================

    // Get all purchase orders for a project
    getPurchaseOrders: builder.query<
      ApiResponse<PurchaseOrderListResponse>,
      { projectId: string; params?: PurchaseOrderListParams }
    >({
      query: ({ projectId, params }) => ({
        url: `/projects/${projectId}/purchase-orders`,
        params,
      }),
      providesTags: ['PurchaseOrder'],
    }),

    // Get single purchase order
    getPurchaseOrder: builder.query<
      ApiResponse<PurchaseOrder>,
      { projectId: string; purchaseOrderId: string }
    >({
      query: ({ projectId, purchaseOrderId }) => ({
        url: `/projects/${projectId}/purchase-orders/${purchaseOrderId}`,
      }),
      providesTags: (_result, _error, { purchaseOrderId }) => [
        { type: 'PurchaseOrder', id: purchaseOrderId },
      ],
    }),

    // Create purchase order
    createPurchaseOrder: builder.mutation<
      ApiResponse<PurchaseOrder>,
      { projectId: string; data: CreatePurchaseOrderRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/purchase-orders`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['PurchaseOrder'],
    }),

    // Update purchase order
    updatePurchaseOrder: builder.mutation<
      ApiResponse<PurchaseOrder>,
      { projectId: string; purchaseOrderId: string; data: UpdatePurchaseOrderRequest }
    >({
      query: ({ projectId, purchaseOrderId, data }) => ({
        url: `/projects/${projectId}/purchase-orders/${purchaseOrderId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { purchaseOrderId }) => [
        'PurchaseOrder',
        { type: 'PurchaseOrder', id: purchaseOrderId },
      ],
    }),

    // Delete purchase order
    deletePurchaseOrder: builder.mutation<
      ApiResponse<void>,
      { projectId: string; purchaseOrderId: string }
    >({
      query: ({ projectId, purchaseOrderId }) => ({
        url: `/projects/${projectId}/purchase-orders/${purchaseOrderId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PurchaseOrder'],
    }),

    // ============================================================================
    // STATUS MANAGEMENT
    // ============================================================================

    // Update status
    updatePurchaseOrderStatus: builder.mutation<
      ApiResponse<PurchaseOrder>,
      { projectId: string; purchaseOrderId: string; data: UpdateStatusRequest }
    >({
      query: ({ projectId, purchaseOrderId, data }) => ({
        url: `/projects/${projectId}/purchase-orders/${purchaseOrderId}/status`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { purchaseOrderId }) => [
        'PurchaseOrder',
        { type: 'PurchaseOrder', id: purchaseOrderId },
      ],
    }),

    // Submit for approval
    submitPurchaseOrderForApproval: builder.mutation<
      ApiResponse<PurchaseOrder>,
      { projectId: string; purchaseOrderId: string }
    >({
      query: ({ projectId, purchaseOrderId }) => ({
        url: `/projects/${projectId}/purchase-orders/${purchaseOrderId}/submit`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { purchaseOrderId }) => [
        'PurchaseOrder',
        { type: 'PurchaseOrder', id: purchaseOrderId },
      ],
    }),

    // Approve or reject
    processPurchaseOrderApproval: builder.mutation<
      ApiResponse<PurchaseOrder>,
      { projectId: string; purchaseOrderId: string; data: ApprovalRequest }
    >({
      query: ({ projectId, purchaseOrderId, data }) => ({
        url: `/projects/${projectId}/purchase-orders/${purchaseOrderId}/approve`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { purchaseOrderId }) => [
        'PurchaseOrder',
        { type: 'PurchaseOrder', id: purchaseOrderId },
      ],
    }),

    // Send to vendor
    sendPurchaseOrder: builder.mutation<
      ApiResponse<PurchaseOrder>,
      { projectId: string; purchaseOrderId: string; data: SendPurchaseOrderRequest }
    >({
      query: ({ projectId, purchaseOrderId, data }) => ({
        url: `/projects/${projectId}/purchase-orders/${purchaseOrderId}/send`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { purchaseOrderId }) => [
        'PurchaseOrder',
        { type: 'PurchaseOrder', id: purchaseOrderId },
      ],
    }),

    // ============================================================================
    // BULK OPERATIONS
    // ============================================================================

    // Bulk status update
    bulkUpdatePurchaseOrderStatus: builder.mutation<
      ApiResponse<{ updated: number; failed: number }>,
      { projectId: string; data: BulkStatusUpdateRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/purchase-orders/bulk-status`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['PurchaseOrder'],
    }),

    // ============================================================================
    // STATISTICS & REPORTING
    // ============================================================================

    // Get summary stats
    getPurchaseOrderStats: builder.query<
      ApiResponse<PurchaseOrderSummaryStats>,
      { projectId: string }
    >({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/purchase-orders/stats`,
      }),
      providesTags: ['PurchaseOrder'],
    }),

    // Get purchase orders by vendor
    getPurchaseOrdersByVendor: builder.query<
      ApiResponse<VendorPurchaseOrderSummary>,
      { projectId: string; vendorName: string }
    >({
      query: ({ projectId, vendorName }) => ({
        url: `/projects/${projectId}/purchase-orders/by-vendor`,
        params: { vendorName },
      }),
      providesTags: ['PurchaseOrder'],
    }),

    // ============================================================================
    // LINE ITEM OPERATIONS
    // ============================================================================

    // Add line item
    addPurchaseOrderLineItem: builder.mutation<
      ApiResponse<PurchaseOrder>,
      { projectId: string; purchaseOrderId: string; data: CreateLineItemRequest }
    >({
      query: ({ projectId, purchaseOrderId, data }) => ({
        url: `/projects/${projectId}/purchase-orders/${purchaseOrderId}/line-items`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { purchaseOrderId }) => [
        'PurchaseOrder',
        { type: 'PurchaseOrder', id: purchaseOrderId },
      ],
    }),

    // Update line item
    updatePurchaseOrderLineItem: builder.mutation<
      ApiResponse<PurchaseOrder>,
      {
        projectId: string;
        purchaseOrderId: string;
        lineItemId: string;
        data: UpdateLineItemRequest;
      }
    >({
      query: ({ projectId, purchaseOrderId, lineItemId, data }) => ({
        url: `/projects/${projectId}/purchase-orders/${purchaseOrderId}/line-items/${lineItemId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { purchaseOrderId }) => [
        'PurchaseOrder',
        { type: 'PurchaseOrder', id: purchaseOrderId },
      ],
    }),

    // Remove line item
    removePurchaseOrderLineItem: builder.mutation<
      ApiResponse<PurchaseOrder>,
      { projectId: string; purchaseOrderId: string; lineItemId: string }
    >({
      query: ({ projectId, purchaseOrderId, lineItemId }) => ({
        url: `/projects/${projectId}/purchase-orders/${purchaseOrderId}/line-items/${lineItemId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { purchaseOrderId }) => [
        'PurchaseOrder',
        { type: 'PurchaseOrder', id: purchaseOrderId },
      ],
    }),

    // ============================================================================
    // RECEIPT OPERATIONS
    // ============================================================================

    // Get all receipts for a purchase order
    getReceipts: builder.query<
      ApiResponse<ReceiptListResponse>,
      { projectId: string; purchaseOrderId: string; params?: ReceiptListParams }
    >({
      query: ({ projectId, purchaseOrderId, params }) => ({
        url: `/projects/${projectId}/purchase-orders/${purchaseOrderId}/receipts`,
        params,
      }),
      providesTags: (_result, _error, { purchaseOrderId }) => [
        { type: 'PurchaseOrder', id: `${purchaseOrderId}-receipts` },
      ],
    }),

    // Get single receipt
    getReceipt: builder.query<
      ApiResponse<Receipt>,
      { projectId: string; purchaseOrderId: string; receiptId: string }
    >({
      query: ({ projectId, purchaseOrderId, receiptId }) => ({
        url: `/projects/${projectId}/purchase-orders/${purchaseOrderId}/receipts/${receiptId}`,
      }),
      providesTags: (_result, _error, { receiptId }) => [
        { type: 'PurchaseOrder', id: `receipt-${receiptId}` },
      ],
    }),

    // Get receipt summary
    getReceiptSummary: builder.query<
      ApiResponse<ReceiptSummary>,
      { projectId: string; purchaseOrderId: string }
    >({
      query: ({ projectId, purchaseOrderId }) => ({
        url: `/projects/${projectId}/purchase-orders/${purchaseOrderId}/receipts/summary`,
      }),
      providesTags: (_result, _error, { purchaseOrderId }) => [
        { type: 'PurchaseOrder', id: `${purchaseOrderId}-receipts` },
      ],
    }),

    // Create receipt
    createReceipt: builder.mutation<
      ApiResponse<Receipt>,
      { projectId: string; purchaseOrderId: string; data: CreateReceiptRequest }
    >({
      query: ({ projectId, purchaseOrderId, data }) => ({
        url: `/projects/${projectId}/purchase-orders/${purchaseOrderId}/receipts`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { purchaseOrderId }) => [
        'PurchaseOrder',
        { type: 'PurchaseOrder', id: purchaseOrderId },
        { type: 'PurchaseOrder', id: `${purchaseOrderId}-receipts` },
      ],
    }),

    // Update receipt
    updateReceipt: builder.mutation<
      ApiResponse<Receipt>,
      { projectId: string; purchaseOrderId: string; receiptId: string; data: UpdateReceiptRequest }
    >({
      query: ({ projectId, purchaseOrderId, receiptId, data }) => ({
        url: `/projects/${projectId}/purchase-orders/${purchaseOrderId}/receipts/${receiptId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { purchaseOrderId, receiptId }) => [
        'PurchaseOrder',
        { type: 'PurchaseOrder', id: purchaseOrderId },
        { type: 'PurchaseOrder', id: `${purchaseOrderId}-receipts` },
        { type: 'PurchaseOrder', id: `receipt-${receiptId}` },
      ],
    }),

    // Delete receipt
    deleteReceipt: builder.mutation<
      ApiResponse<void>,
      { projectId: string; purchaseOrderId: string; receiptId: string }
    >({
      query: ({ projectId, purchaseOrderId, receiptId }) => ({
        url: `/projects/${projectId}/purchase-orders/${purchaseOrderId}/receipts/${receiptId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { purchaseOrderId }) => [
        'PurchaseOrder',
        { type: 'PurchaseOrder', id: purchaseOrderId },
        { type: 'PurchaseOrder', id: `${purchaseOrderId}-receipts` },
      ],
    }),

    // Confirm receipt
    confirmReceipt: builder.mutation<
      ApiResponse<Receipt>,
      {
        projectId: string;
        purchaseOrderId: string;
        receiptId: string;
        data?: ConfirmReceiptRequest;
      }
    >({
      query: ({ projectId, purchaseOrderId, receiptId, data }) => ({
        url: `/projects/${projectId}/purchase-orders/${purchaseOrderId}/receipts/${receiptId}/confirm`,
        method: 'POST',
        body: data || {},
      }),
      invalidatesTags: (_result, _error, { purchaseOrderId, receiptId }) => [
        'PurchaseOrder',
        { type: 'PurchaseOrder', id: purchaseOrderId },
        { type: 'PurchaseOrder', id: `${purchaseOrderId}-receipts` },
        { type: 'PurchaseOrder', id: `receipt-${receiptId}` },
      ],
    }),

    // Void receipt
    voidReceipt: builder.mutation<
      ApiResponse<Receipt>,
      { projectId: string; purchaseOrderId: string; receiptId: string; reason: string }
    >({
      query: ({ projectId, purchaseOrderId, receiptId, reason }) => ({
        url: `/projects/${projectId}/purchase-orders/${purchaseOrderId}/receipts/${receiptId}/void`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: (_result, _error, { purchaseOrderId, receiptId }) => [
        'PurchaseOrder',
        { type: 'PurchaseOrder', id: purchaseOrderId },
        { type: 'PurchaseOrder', id: `${purchaseOrderId}-receipts` },
        { type: 'PurchaseOrder', id: `receipt-${receiptId}` },
      ],
    }),

    // ============================================================================
    // CHANGE ORDER OPERATIONS
    // ============================================================================

    // Get all change orders for a purchase order
    getChangeOrders: builder.query<
      ApiResponse<ChangeOrderListResponse>,
      { projectId: string; purchaseOrderId: string; params?: ChangeOrderListParams }
    >({
      query: ({ projectId, purchaseOrderId, params }) => ({
        url: `/projects/${projectId}/purchase-orders/${purchaseOrderId}/change-orders`,
        params,
      }),
      providesTags: (_result, _error, { purchaseOrderId }) => [
        { type: 'PurchaseOrder', id: `${purchaseOrderId}-change-orders` },
      ],
    }),

    // Get single change order
    getChangeOrder: builder.query<
      ApiResponse<ChangeOrder>,
      { projectId: string; purchaseOrderId: string; changeOrderId: string }
    >({
      query: ({ projectId, purchaseOrderId, changeOrderId }) => ({
        url: `/projects/${projectId}/purchase-orders/${purchaseOrderId}/change-orders/${changeOrderId}`,
      }),
      providesTags: (_result, _error, { changeOrderId }) => [
        { type: 'PurchaseOrder', id: `change-order-${changeOrderId}` },
      ],
    }),

    // Get change order summary
    getChangeOrderSummary: builder.query<
      ApiResponse<ChangeOrderSummary>,
      { projectId: string; purchaseOrderId: string }
    >({
      query: ({ projectId, purchaseOrderId }) => ({
        url: `/projects/${projectId}/purchase-orders/${purchaseOrderId}/change-orders/summary`,
      }),
      providesTags: (_result, _error, { purchaseOrderId }) => [
        { type: 'PurchaseOrder', id: `${purchaseOrderId}-change-orders` },
      ],
    }),

    // Create change order
    createChangeOrder: builder.mutation<
      ApiResponse<ChangeOrder>,
      { projectId: string; purchaseOrderId: string; data: CreateChangeOrderRequest }
    >({
      query: ({ projectId, purchaseOrderId, data }) => ({
        url: `/projects/${projectId}/purchase-orders/${purchaseOrderId}/change-orders`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { purchaseOrderId }) => [
        'PurchaseOrder',
        { type: 'PurchaseOrder', id: purchaseOrderId },
        { type: 'PurchaseOrder', id: `${purchaseOrderId}-change-orders` },
      ],
    }),

    // Update change order
    updateChangeOrder: builder.mutation<
      ApiResponse<ChangeOrder>,
      {
        projectId: string;
        purchaseOrderId: string;
        changeOrderId: string;
        data: UpdateChangeOrderRequest;
      }
    >({
      query: ({ projectId, purchaseOrderId, changeOrderId, data }) => ({
        url: `/projects/${projectId}/purchase-orders/${purchaseOrderId}/change-orders/${changeOrderId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { purchaseOrderId, changeOrderId }) => [
        'PurchaseOrder',
        { type: 'PurchaseOrder', id: purchaseOrderId },
        { type: 'PurchaseOrder', id: `${purchaseOrderId}-change-orders` },
        { type: 'PurchaseOrder', id: `change-order-${changeOrderId}` },
      ],
    }),

    // Delete change order
    deleteChangeOrder: builder.mutation<
      ApiResponse<void>,
      { projectId: string; purchaseOrderId: string; changeOrderId: string }
    >({
      query: ({ projectId, purchaseOrderId, changeOrderId }) => ({
        url: `/projects/${projectId}/purchase-orders/${purchaseOrderId}/change-orders/${changeOrderId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { purchaseOrderId }) => [
        'PurchaseOrder',
        { type: 'PurchaseOrder', id: purchaseOrderId },
        { type: 'PurchaseOrder', id: `${purchaseOrderId}-change-orders` },
      ],
    }),

    // Submit change order for approval
    submitChangeOrder: builder.mutation<
      ApiResponse<ChangeOrder>,
      { projectId: string; purchaseOrderId: string; changeOrderId: string }
    >({
      query: ({ projectId, purchaseOrderId, changeOrderId }) => ({
        url: `/projects/${projectId}/purchase-orders/${purchaseOrderId}/change-orders/${changeOrderId}/submit`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { purchaseOrderId, changeOrderId }) => [
        'PurchaseOrder',
        { type: 'PurchaseOrder', id: purchaseOrderId },
        { type: 'PurchaseOrder', id: `${purchaseOrderId}-change-orders` },
        { type: 'PurchaseOrder', id: `change-order-${changeOrderId}` },
      ],
    }),

    // Approve change order
    approveChangeOrder: builder.mutation<
      ApiResponse<ChangeOrder>,
      {
        projectId: string;
        purchaseOrderId: string;
        changeOrderId: string;
        data?: ApproveChangeOrderRequest;
      }
    >({
      query: ({ projectId, purchaseOrderId, changeOrderId, data }) => ({
        url: `/projects/${projectId}/purchase-orders/${purchaseOrderId}/change-orders/${changeOrderId}/approve`,
        method: 'POST',
        body: data || {},
      }),
      invalidatesTags: (_result, _error, { purchaseOrderId, changeOrderId }) => [
        'PurchaseOrder',
        { type: 'PurchaseOrder', id: purchaseOrderId },
        { type: 'PurchaseOrder', id: `${purchaseOrderId}-change-orders` },
        { type: 'PurchaseOrder', id: `change-order-${changeOrderId}` },
      ],
    }),

    // Reject change order
    rejectChangeOrder: builder.mutation<
      ApiResponse<ChangeOrder>,
      {
        projectId: string;
        purchaseOrderId: string;
        changeOrderId: string;
        data: RejectChangeOrderRequest;
      }
    >({
      query: ({ projectId, purchaseOrderId, changeOrderId, data }) => ({
        url: `/projects/${projectId}/purchase-orders/${purchaseOrderId}/change-orders/${changeOrderId}/reject`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { purchaseOrderId, changeOrderId }) => [
        'PurchaseOrder',
        { type: 'PurchaseOrder', id: purchaseOrderId },
        { type: 'PurchaseOrder', id: `${purchaseOrderId}-change-orders` },
        { type: 'PurchaseOrder', id: `change-order-${changeOrderId}` },
      ],
    }),
  }),
});

/**
 * Export hooks for use in components
 */
export const {
  // CRUD
  useGetPurchaseOrdersQuery,
  useGetPurchaseOrderQuery,
  useCreatePurchaseOrderMutation,
  useUpdatePurchaseOrderMutation,
  useDeletePurchaseOrderMutation,
  // Status Management
  useUpdatePurchaseOrderStatusMutation,
  useSubmitPurchaseOrderForApprovalMutation,
  useProcessPurchaseOrderApprovalMutation,
  useSendPurchaseOrderMutation,
  // Bulk Operations
  useBulkUpdatePurchaseOrderStatusMutation,
  // Statistics
  useGetPurchaseOrderStatsQuery,
  useGetPurchaseOrdersByVendorQuery,
  // Line Items
  useAddPurchaseOrderLineItemMutation,
  useUpdatePurchaseOrderLineItemMutation,
  useRemovePurchaseOrderLineItemMutation,
  // Receipts
  useGetReceiptsQuery,
  useGetReceiptQuery,
  useGetReceiptSummaryQuery,
  useCreateReceiptMutation,
  useUpdateReceiptMutation,
  useDeleteReceiptMutation,
  useConfirmReceiptMutation,
  useVoidReceiptMutation,
  // Change Orders
  useGetChangeOrdersQuery,
  useGetChangeOrderQuery,
  useGetChangeOrderSummaryQuery,
  useCreateChangeOrderMutation,
  useUpdateChangeOrderMutation,
  useDeleteChangeOrderMutation,
  useSubmitChangeOrderMutation,
  useApproveChangeOrderMutation,
  useRejectChangeOrderMutation,
} = purchaseOrderApi;

/**
 * Re-export types for convenience
 */
export type {
  PurchaseOrderListParams,
  ReceiptListParams,
  ChangeOrderListParams,
} from '../types/purchaseOrder';

// ============================================================================
// PDF UTILITIES
// ============================================================================

/**
 * Get the PDF download URL for a purchase order
 */
export function getPurchaseOrderPdfUrl(projectId: string, purchaseOrderId: string): string {
  return `/api/projects/${projectId}/purchase-orders/${purchaseOrderId}/pdf`;
}

/**
 * Get the PDF preview URL for a purchase order
 */
export function getPurchaseOrderPdfPreviewUrl(projectId: string, purchaseOrderId: string): string {
  return `/api/projects/${projectId}/purchase-orders/${purchaseOrderId}/pdf/preview`;
}

/**
 * Download purchase order as PDF
 */
export async function downloadPurchaseOrderPdf(
  projectId: string,
  purchaseOrderId: string,
  poNumber: string
): Promise<void> {
  const token = localStorage.getItem('auth_token');
  const url = getPurchaseOrderPdfUrl(projectId, purchaseOrderId);

  const response = await fetch(url, {
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to download PDF');
  }

  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = `PO-${poNumber}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
}
